import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../../../store';
import type { SelectedFiltersState } from '../interfaces/SelectedFilters';
import {
  getChiaveRichiestaAppuntamenti,
  ParametriRichiestaAppuntamenti,
} from './ChiaveRichiestaAppuntamenti';

/**
 * Filtri dei giornalieri Studio e Utente che decidono quali appuntamenti
 * caricare: il giorno, il punto vendita e i servizi.
 */
export type FiltriAppuntamentiGiornaliero = Pick<
  SelectedFiltersState,
  'selectedDate' | 'selectedServices' | 'selectedPointOfSale'
> & {
  /** Email del cliente filtrato, se c'è */
  email?: string;
};

/**
 * Parametri della GetAll dei giornalieri Studio e Utente: un giorno solo
 * (vista 'Day' anche nell'Orizzontale), il punto vendita e i servizi, senza
 * utenti né studi, che sono le colonne. Li usano il caricamento e il confronto
 * con la lista in redux (selectAppuntamentiStudioPronti,
 * selectAppuntamentiUtentePronti): costruiti in un posto solo, la chiave della
 * pagina e quella della richiesta coincidono.
 */
export function getParametriAppuntamentiGiornaliero({
  selectedDate,
  selectedServices,
  selectedPointOfSale,
  email,
}: FiltriAppuntamentiGiornaliero): ParametriRichiestaAppuntamenti {
  return {
    selectedDate,
    checkedServices: selectedServices,
    checkedUsers: [],
    checkedPointOfSales: selectedPointOfSale ? [selectedPointOfSale.id] : [],
    checkedStudi: [],
    view: 'Day',
    email,
  };
}

// Selettore della prontezza per lo store di un giornaliero, creato una volta
// sola a livello di modulo: la chiave si ricalcola solo quando cambia uno dei
// filtri, non a ogni azione dello store
const creaSelectAppuntamentiPronti = (
  selectFiltri: (state: RootState) => SelectedFiltersState
) => {
  const selectChiave = createSelector(
    [
      (state: RootState) => selectFiltri(state).selectedDate,
      (state: RootState) => selectFiltri(state).selectedServices,
      (state: RootState) => selectFiltri(state).selectedPointOfSale,
      (state: RootState) => selectFiltri(state).client?.email,
    ],
    (selectedDate, selectedServices, selectedPointOfSale, email) =>
      getChiaveRichiestaAppuntamenti(
        getParametriAppuntamentiGiornaliero({
          selectedDate,
          selectedServices,
          selectedPointOfSale,
          email,
        })
      )
  );
  return (state: RootState): boolean =>
    state.bluBookingaAppointments.chiaveRichiesta === selectChiave(state);
};

/**
 * La lista appuntamenti in redux è quella del giorno e dei filtri correnti del
 * giornaliero Studio. Il caricamento finito non basta: nei 50 ms prima della
 * richiesta, e finché la risposta nuova non arriva, la lista è quella vecchia
 * (anche quella di un altro calendario: la lista è una sola). Dopo un errore la
 * lista vuota ha la chiave giusta, quindi è pronta.
 */
export const selectAppuntamentiStudioPronti = creaSelectAppuntamentiPronti(
  (state) => state.bluBookingStudioCalendar
);

/** Come selectAppuntamentiStudioPronti, per il giornaliero Utente. */
export const selectAppuntamentiUtentePronti = creaSelectAppuntamentiPronti(
  (state) => state.bluBookingUserCalendar
);

/**
 * Appuntamenti delle colonne di un giornaliero: quelli la cui risorsa (studio o
 * utente, nel campo indicato) è una delle colonne. Syncfusion non disegna gli
 * altri, che non devono allargare la finestra «Solo orario». Senza colonne
 * (calendario non ancora raggruppato) contano tutti, come li disegna Syncfusion.
 */
export function getAppuntamentiDelleColonne<T>(
  appuntamenti: readonly T[],
  campo: keyof T,
  colonne: readonly string[] | undefined
): readonly T[] {
  if (colonne === undefined) return appuntamenti;
  const inColonna = new Set(colonne);
  return appuntamenti.filter((appuntamento) => {
    const risorsa: unknown = appuntamento[campo];
    return typeof risorsa === 'string' && inColonna.has(risorsa);
  });
}
